import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, BookOpen, Quote, Moon, Sparkles, Play, Pause, Plus } from "lucide-react";
import { DAYS, ARRIVAL } from "@/lib/mentalPlan";

const CREAM = "#FAF7FD";
const INK = "#2E2640";
const STAGES = ["Arrive", "Look at it", "Do one thing"];

/* --------------------------------------------------- a breathing pacer */
function Breath({ hue, onEnough }) {
  const [running, setRunning] = useState(false);
  const [cycle, setCycle] = useState(0);
  const [phase, setPhase] = useState("in");

  useEffect(() => {
    if (!running) return;
    const dur = phase === "in" ? 4000 : 6000;
    const t = setTimeout(() => {
      if (phase === "in") setPhase("out");
      else {
        setPhase("in");
        setCycle((c) => {
          const n = c + 1;
          if (n >= 4) { setRunning(false); onEnough?.(); }
          return n;
        });
      }
    }, dur);
    return () => clearTimeout(t);
  }, [running, phase, onEnough]);

  const done = cycle >= 4;
  return (
    <div className="flex flex-col items-center" data-testid="breath-pacer">
      <button onClick={() => setRunning(!running)} className="relative w-[210px] h-[210px] sm:w-[250px] sm:h-[250px] flex items-center justify-center" data-testid="breath-toggle">
        {[0, 1, 2].map((r) => (
          <motion.span key={r} className="absolute rounded-full" style={{ inset: r * 16, border: `1px solid ${hue}${r === 0 ? "55" : "26"}` }}
            animate={running ? { scale: phase === "in" ? 1.1 : 0.86, opacity: phase === "in" ? 0.95 : 0.45 } : { scale: 1, opacity: 0.5 }}
            transition={{ duration: phase === "in" ? 4 : 6, ease: "easeInOut", delay: r * 0.12 }} />
        ))}
        <motion.span className="absolute rounded-full" style={{ inset: 44, background: `radial-gradient(circle at 40% 35%, ${hue}D9, ${hue}77)`, boxShadow: `0 30px 70px -30px ${hue}` }}
          animate={running ? { scale: phase === "in" ? 1.14 : 0.8 } : { scale: [1, 1.04, 1] }}
          transition={running ? { duration: phase === "in" ? 4 : 6, ease: "easeInOut" } : { duration: 5, repeat: Infinity }} />
        <span className="relative z-10 text-white text-center">
          {running ? (
            <span className="font-display text-[22px]">{phase === "in" ? "breathe in" : "let it out"}</span>
          ) : (
            <span className="inline-flex items-center gap-2 font-display text-[19px]">{done ? <Check size={16} /> : <Play size={15} />}{done ? "that is enough" : cycle ? "carry on" : "four breaths"}</span>
          )}
        </span>
      </button>
      <div className="flex gap-2 mt-6">
        {[0, 1, 2, 3].map((n) => (
          <span key={n} className="w-8 h-[3px] rounded-full transition-colors" style={{ background: n < cycle ? hue : "rgba(46,38,64,0.14)" }} />
        ))}
      </div>
      <p className="text-[12px] mt-3" style={{ color: "rgba(46,38,64,0.45)" }}>{running ? "tap to stop" : "in for four, out for six"}</p>
    </div>
  );
}

/* ------------------------------------------------- one line at a time */
function Reads({ lines, hue }) {
  const [shown, setShown] = useState(1);
  return (
    <div>
      <p className="text-[10px] tracking-[0.2em] uppercase" style={{ color: "rgba(46,38,64,0.4)" }}>Today, in {lines.length} lines</p>
      <div className="mt-5 space-y-5">
        {lines.slice(0, shown).map((l, n) => (
          <motion.p key={n} initial={{ opacity: 0, y: 18, filter: "blur(6px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-[23px] sm:text-[30px] leading-[1.3]" style={{ color: n === shown - 1 ? INK : "rgba(46,38,64,0.42)" }} data-testid={`read-line-${n}`}>
            {l}
          </motion.p>
        ))}
      </div>
      {shown < lines.length && (
        <button onClick={() => setShown(shown + 1)} className="mt-7 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-semibold"
          style={{ background: `${hue}16`, color: hue }} data-testid="read-more">
          <Plus size={13} /> one more line
        </button>
      )}
    </div>
  );
}

/* ------------------------------------------------------- a live timer */
function Timer({ minutes, hue, done, onDone }) {
  const total = (minutes || 10) * 60;
  const [left, setLeft] = useState(total);
  const [on, setOn] = useState(false);
  const ref = useRef(onDone);
  ref.current = onDone;
  useEffect(() => {
    if (!on) return;
    const t = setInterval(() => setLeft((l) => {
      if (l <= 1) { setOn(false); ref.current?.(); return 0; }
      return l - 1;
    }), 1000);
    return () => clearInterval(t);
  }, [on]);
  const pct = 1 - left / total;
  const mm = String(Math.floor(left / 60)).padStart(2, "0");
  const ss = String(left % 60).padStart(2, "0");
  return (
    <div className="mt-6 flex items-center gap-4 rounded-[1.4rem] px-5 py-4" style={{ background: "#fff", border: "1px solid rgba(46,38,64,0.09)" }} data-testid="small-timer">
      <button onClick={() => setOn(!on)} className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 text-white" style={{ background: hue }} data-testid="small-timer-toggle">
        {on ? <Pause size={16} /> : <Play size={16} />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between">
          <p className="font-display text-2xl tabular-nums" style={{ color: INK }}>{mm}:{ss}</p>
          <p className="text-[11px]" style={{ color: "rgba(46,38,64,0.45)" }}>{done ? "logged" : on ? "running" : "start it when you are ready"}</p>
        </div>
        <div className="h-1.5 rounded-full mt-2 overflow-hidden" style={{ background: "rgba(46,38,64,0.09)" }}>
          <motion.div className="h-full rounded-full" style={{ background: hue }} animate={{ width: `${pct * 100}%` }} transition={{ duration: 0.4 }} />
        </div>
      </div>
    </div>
  );
}

/* ============================================================== a day */
export default function DayFlow({ dayIndex, core, name, state, onSave, onClose, onComplete }) {
  const day = DAYS[dayIndex];
  const [i, setI] = useState(0);
  const [arrival, setArrival] = useState(state?.arrival || null);
  const [words, setWords] = useState(state?.words || "");
  const [smallDone, setSmallDone] = useState(!!state?.smallDone);
  const [handsDone, setHandsDone] = useState(!!state?.handsDone);

  const steps = [
    { k: "arrival", stage: 0 },
    { k: "breath", stage: 0 },
    { k: "reads", stage: 1 },
    { k: "keep", stage: 1 },
    { k: "small", stage: 2 },
    { k: "hands", stage: 2 },
    { k: "forYou", stage: 2 },
    { k: "close", stage: 2 },
  ];
  const s = steps[i];
  const last = i === steps.length - 1;

  const save = (p = {}) => onSave({ arrival, words, smallDone, handsDone, ...p });
  const next = () => { save(); last ? onComplete() : setI(i + 1); };

  // keep the handler fresh in a ref so the listener is bound once, not on every render
  const nextRef = useRef(next);
  nextRef.current = next;
  useEffect(() => {
    const k = (e) => {
      if (["TEXTAREA", "INPUT"].includes(e.target?.tagName)) return;
      if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); nextRef.current(); }
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => onSave({ arrival, words, smallDone, handsDone }), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrival, smallDone, handsDone, words]);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[90] overflow-y-auto" style={{ background: CREAM }} data-testid={`day-flow-${day.n}`}>
      <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(58% 44% at 84% 0%, ${core.hue}2E, transparent 70%), radial-gradient(52% 42% at 0% 100%, ${core.hue}1A, transparent 70%)` }} />

      <div className="relative max-w-2xl mx-auto px-5 sm:px-8 py-8 min-h-full flex flex-col">
        <div className="flex items-start justify-between gap-4">
          <button onClick={() => { save(); onClose(); }} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[12px] font-semibold transition-colors hover:-translate-x-0.5" style={{ background: "rgba(46,38,64,0.06)", color: INK }} data-testid="day-exit">
            <ArrowLeft size={13} /> Back to my path
          </button>
          <div className="text-right">
            <p className="text-[10px] tracking-[0.2em] uppercase font-semibold" style={{ color: core.hue }}>Week one · day {day.n}</p>
            <h2 className="font-display text-lg mt-0.5" style={{ color: INK }}>{day.title}</h2>
          </div>
        </div>

        {/* three stages */}
        <div className="flex items-center gap-2.5 mt-7">
          {STAGES.map((label, n) => {
            const on = s.stage === n;
            const past = s.stage > n;
            return (
              <div key={label} className="flex-1 min-w-0">
                <div className="h-[5px] rounded-full overflow-hidden" style={{ background: "rgba(46,38,64,0.09)" }}>
                  <motion.span className="block h-full rounded-full" initial={false}
                    animate={{ width: past ? "100%" : on ? `${((i - steps.findIndex((x) => x.stage === n) + 1) / steps.filter((x) => x.stage === n).length) * 100}%` : "0%" }}
                    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }} style={{ background: core.hue }} />
                </div>
                <p className="text-[10px] tracking-[0.14em] uppercase mt-2 truncate" style={{ color: on ? core.hue : "rgba(46,38,64,0.35)", fontWeight: on ? 600 : 400 }}>{label}</p>
              </div>
            );
          })}
        </div>

        <div className="flex-1 flex items-center py-10">
          <AnimatePresence mode="wait">
            <motion.div key={i} initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }} className="w-full">
              {s.k === "arrival" && (
                <div>
                  <h3 className="font-display text-3xl sm:text-4xl leading-tight" style={{ color: INK }}>How are you arriving{name ? `, ${name}` : ""}?</h3>
                  <div className="grid grid-cols-3 gap-2.5 mt-7">
                    {ARRIVAL.map((a) => (
                      <motion.button key={a.key} whileTap={{ scale: 0.97 }} onClick={() => setArrival(a.key)} className="rounded-2xl px-3 py-5 transition-all"
                        style={arrival === a.key ? { background: "#fff", border: `1.5px solid ${a.tone}`, boxShadow: `0 16px 34px -18px ${a.tone}` } : { background: "rgba(255,255,255,0.7)", border: "1px solid rgba(46,38,64,0.08)" }}
                        data-testid={`arrival-${a.key}`}>
                        <motion.span className="block w-7 h-7 mx-auto rounded-full" style={{ background: `${a.tone}2E`, border: `1.5px solid ${a.tone}` }}
                          animate={arrival === a.key ? { scale: [1, 1.16, 1] } : {}} transition={{ duration: 1.4 }} />
                        <span className="block text-[12px] font-medium mt-2.5" style={{ color: INK }}>{a.label}</span>
                      </motion.button>
                    ))}
                  </div>
                  <textarea value={words} onChange={(e) => setWords(e.target.value)} rows={2} placeholder="A few words, if you want. Optional, always."
                    className="mt-4 w-full rounded-2xl px-4 py-3.5 text-[15px] outline-none" style={{ background: "#fff", border: "1px solid rgba(46,38,64,0.1)", color: INK }} data-testid="arrival-words" />
                  <div className="mt-4 rounded-2xl px-4 py-3.5" style={{ background: `${core.hue}14` }}>
                    <p className="text-[10px] tracking-[0.16em] uppercase font-semibold flex items-center gap-1.5" style={{ color: core.hue }}><Sparkles size={11} /> woven in for you</p>
                    <p className="text-sm mt-1" style={{ color: INK }}>{core.woven}</p>
                  </div>
                </div>
              )}

              {s.k === "breath" && (
                <div className="flex flex-col items-center text-center">
                  <p className="text-[10px] tracking-[0.2em] uppercase" style={{ color: "rgba(46,38,64,0.4)" }}>Before anything else</p>
                  <h3 className="font-display text-3xl sm:text-4xl leading-tight mt-2 mb-9" style={{ color: INK }}>Four breaths, then we start</h3>
                  <Breath hue={core.hue} />
                </div>
              )}

              {s.k === "reads" && <Reads lines={day.reads} hue={core.hue} />}

              {s.k === "keep" && (
                <div className="flex flex-col items-center text-center">
                  <motion.div initial={{ rotate: -3, scale: 0.94, opacity: 0 }} animate={{ rotate: -1.5, scale: 1, opacity: 1 }} transition={{ type: "spring", damping: 14 }}
                    className="rounded-[1.4rem] px-7 py-8 max-w-md relative overflow-hidden" style={{ background: `linear-gradient(150deg, ${core.hue}, #4B3B7A)`, boxShadow: `0 30px 60px -28px ${core.hue}` }}>
                    <span className="absolute -right-12 -top-12 w-40 h-40 rounded-full halo" style={{ background: "radial-gradient(circle, rgba(232,184,75,0.35), transparent 70%)" }} />
                    <Quote size={16} className="text-white/50 relative" />
                    <p className="font-display text-2xl leading-snug mt-3 text-white relative">{day.keep}</p>
                  </motion.div>
                  <p className="text-sm mt-5" style={{ color: "rgba(46,38,64,0.45)" }}>Keep this one in your pocket today.</p>
                </div>
              )}

              {(s.k === "small" || s.k === "hands") && (() => {
                const b = s.k === "small" ? day.small : day.hands;
                const done = s.k === "small" ? smallDone : handsDone;
                const set = s.k === "small" ? setSmallDone : setHandsDone;
                const mark = () => { set(true); save({ [s.k === "small" ? "smallDone" : "handsDone"]: true }); };
                return (
                  <div>
                    <p className="text-[10px] tracking-[0.2em] uppercase" style={{ color: "rgba(46,38,64,0.4)" }}>
                      {s.k === "small" ? "One small thing to do" : "Something with your hands"}{b.minutes ? ` · ${b.minutes} minutes` : ""}
                    </p>
                    <h3 className="font-display text-3xl sm:text-4xl leading-tight mt-3" style={{ color: INK }}>{b.title}</h3>
                    <p className="text-[16px] mt-3.5 leading-relaxed" style={{ color: "rgba(46,38,64,0.7)" }}>{b.text}</p>
                    {s.k === "small" && <Timer minutes={b.minutes} hue={core.hue} done={done} onDone={mark} />}
                    <button onClick={() => { const v = !done; set(v); save({ [s.k === "small" ? "smallDone" : "handsDone"]: v }); }}
                      className="mt-6 inline-flex items-center gap-2.5 rounded-full px-5 py-3 text-sm font-semibold transition-transform hover:-translate-y-0.5"
                      style={done ? { background: core.hue, color: "#fff" } : { background: "#fff", color: INK, border: "1px solid rgba(46,38,64,0.12)" }}
                      data-testid={`${s.k}-done`}>
                      <span className="w-4 h-4 rounded-full flex items-center justify-center" style={done ? { background: "rgba(255,255,255,0.25)" } : { border: "1.5px solid rgba(46,38,64,0.25)" }}>
                        {done && <Check size={10} />}
                      </span>
                      {done ? "Done" : "I did this"}
                    </button>
                  </div>
                );
              })()}

              {s.k === "forYou" && (
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase" style={{ color: "rgba(46,38,64,0.4)" }}>Chosen for you, {name || "you"}</p>
                  <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
                    className="mt-4 rounded-[1.4rem] p-6 flex gap-5 items-start" style={{ background: "#fff", border: "1px solid rgba(46,38,64,0.1)" }}>
                    <motion.div className="w-[54px] h-[76px] rounded-md shrink-0" initial={{ rotate: -6 }} animate={{ rotate: -3 }}
                      style={{ background: `linear-gradient(150deg, ${core.hue}, ${core.hue}88)`, boxShadow: "0 12px 24px -12px rgba(0,0,0,0.45)" }} />
                    <div>
                      <p className="font-display text-xl leading-tight" style={{ color: INK }}>{core.book.title}</p>
                      <p className="text-[13px] mt-0.5" style={{ color: "rgba(46,38,64,0.5)" }}>{core.book.author}</p>
                      <p className="text-sm mt-2.5" style={{ color: "rgba(46,38,64,0.7)" }}>{core.book.why}</p>
                    </div>
                  </motion.div>
                  <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }}
                    className="mt-4 rounded-[1.4rem] p-6" style={{ background: `${core.hue}16` }}>
                    <p className="text-[10px] tracking-[0.16em] uppercase font-semibold" style={{ color: core.hue }}>One change to try</p>
                    <p className="font-display text-xl mt-1.5 leading-snug" style={{ color: INK }}>{day.change}</p>
                  </motion.div>
                </div>
              )}

              {s.k === "close" && (
                <div>
                  <Moon size={22} style={{ color: core.hue }} />
                  <p className="font-display text-[28px] sm:text-[36px] leading-[1.24] mt-4" style={{ color: INK }}>{day.close}</p>
                  <p className="text-sm mt-4" style={{ color: "rgba(46,38,64,0.5)" }}>Day {day.n + 1 > 7 ? "one of week two" : day.n + 1} opens when you close this one.</p>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="flex items-center justify-between pb-4">
          <button onClick={() => (i > 0 ? setI(i - 1) : (save(), onClose()))} className="text-xs inline-flex items-center gap-1.5" style={{ color: "rgba(46,38,64,0.45)" }} data-testid="day-back">
            <ArrowLeft size={12} /> {i > 0 ? "back" : "leave"}
          </button>
          <button onClick={next} className="inline-flex items-center gap-2.5 rounded-full px-7 py-3.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
            style={{ background: core.hue, boxShadow: `0 18px 40px -18px ${core.hue}` }} data-testid="day-next">
            {last ? "Close the day" : "Continue"} <ArrowRight size={15} />
          </button>
        </div>
      </div>
    </motion.div>
  );
}
